import type { DebtData, PaymentPlanConfiguration, PaymentPlanTier } from '@/types';
import type { TransactionItem } from '@/types/core-api';
import type { DebtPaymentPlan } from '@/types/domain/debt';
import type { OphelosPaymentPlanResponse } from '@/types/payment-plan';

/** The plan a payment was made under — absent on a one-off payment. The payment webhook writes it as `{id, index}`. */
export function transactionPlanId(tx: TransactionItem): string | undefined {
  return (tx.metadata?.payment_plan as { id?: string } | undefined)?.id;
}

/**
 * Select the appropriate payment plan tier based on debt amount
 * @param debtAmount - The total debt amount
 * @param config - Payment plan configuration
 * @returns The matching tier or null if no tier matches
 */
export function selectPaymentTier(
  debtAmount: number,
  config: PaymentPlanConfiguration | undefined,
): PaymentPlanTier | null {
  if (!config) return null;

  // Handle legacy format (auto-migrate to tier format)
  if (config.maxDurationMonths !== undefined && !config.tiers) {
    return {
      id: 'tier_legacy',
      debtThreshold: 0,
      maxDurationMonths: config.maxDurationMonths,
    };
  }

  // Check minimum eligibility
  if (config.minEligibleDebt && debtAmount < config.minEligibleDebt) {
    return null;
  }

  // No tiers configured
  if (!config.tiers || config.tiers.length === 0) {
    return null;
  }

  // Find matching tier (largest threshold ≤ debt)
  const sortedTiers = [...config.tiers].sort((a, b) => b.debtThreshold - a.debtThreshold);

  for (const tier of sortedTiers) {
    if (debtAmount >= tier.debtThreshold) {
      return tier;
    }
  }

  return null; // Should never happen if first tier starts at 0
}

/**
 * Check if debt amount is eligible for payment plans
 * @param debtAmount - The total debt amount
 * @param config - Payment plan configuration
 * @returns True if payment plans are available for this debt
 */
export function isPaymentPlanEligible(
  debtAmount: number,
  config: PaymentPlanConfiguration | undefined,
): boolean {
  return selectPaymentTier(debtAmount, config) !== null;
}

/**
 * A scheduled full/partial payment is created as a one-installment plan on the
 * provider side, but should be treated as a payment in progress, not a payment
 * plan. True when the debt has an active such plan.
 */
export function isScheduledPayment(plan: DebtPaymentPlan | null): boolean {
  return Boolean(plan && plan.status === 'active' && plan.fullPlan?.is_scheduled_payment);
}

/** The date (YYYY-MM-DD) an active scheduled single payment will be taken, or null. */
export function scheduledPaymentDate(plan: DebtPaymentPlan | null): string | null {
  return isScheduledPayment(plan) ? (plan?.fullPlan?.scheduled_date ?? null) : null;
}

/** The payment method of an active scheduled single payment, or null. */
export function scheduledPaymentMethod(plan: DebtPaymentPlan | null): string | null {
  return isScheduledPayment(plan) ? (plan?.fullPlan?.payment_method ?? null) : null;
}

/** The amount (major units) of an active scheduled single payment, or null. */
export function scheduledPaymentAmount(plan: DebtPaymentPlan | null): number | null {
  if (!isScheduledPayment(plan)) return null;
  const subunits = plan?.fullPlan?.schedule?.[0]?.subunits;
  return typeof subunits === 'number' ? subunits / 100 : null;
}

/**
 * Whether a case-level plan bars account-wide payment. One arranged case closes
 * the account surface even when other cases are free to pay: `_check_cross_scope_exclusion`
 * refuses an account-scoped plan while any case on the account has one.
 *
 * A boolean rather than which-and-where, because several cases can each have one at once —
 * case-level plans exclude only account scope, never each other. That is also why the
 * case-level block card is generic while the account-level ones name their type: account
 * scope is capped at one plan by
 * `payment_plans_one_active_account_level_per_account`, case scope has no such cap.
 *
 * Reads `paymentPlan`, which is case-scoped only, so an account plan's members never match.
 */
export function hasCaseLevelPlan(debts: DebtData[]): boolean {
  return debts.some((d) => d.paymentPlan);
}

/**
 * Which active account payment blocks every other payment, or null when none
 * does. It holds cases added after it started, so never check membership.
 */
export function activeAccountPayment(
  plan: OphelosPaymentPlanResponse | null,
): 'scheduled' | 'plan' | null {
  if (plan?.status !== 'active') return null;
  return plan.is_scheduled_payment ? 'scheduled' : 'plan';
}

/** The date (YYYY-MM-DD) an active scheduled account single payment will be taken, or null. */
export function scheduledAccountPaymentDate(plan: OphelosPaymentPlanResponse | null): string | null {
  return activeAccountPayment(plan) === 'scheduled' ? (plan?.scheduled_date ?? null) : null;
}

/** The amount (major units) of an active scheduled account single payment, or null. */
export function scheduledAccountPaymentAmount(plan: OphelosPaymentPlanResponse | null): number | null {
  if (activeAccountPayment(plan) !== 'scheduled') return null;
  const subunits = plan?.schedule?.[0]?.subunits;
  return typeof subunits === 'number' ? subunits / 100 : null;
}

/** The payment method of an active scheduled account single payment, or null. */
export function scheduledAccountPaymentMethod(plan: OphelosPaymentPlanResponse | null): string | null {
  return activeAccountPayment(plan) === 'scheduled' ? (plan?.payment_method ?? null) : null;
}
